import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import * as I from 'immutable';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import React from 'react';
import {Column, useFlexLayout, useTable} from 'react-table';

import Modal from 'app/components/Modal/Modal';
import {SavedCardsList} from 'app/components/PaymentMethods/SavedCardsList';
import * as Remote from 'app/modules/Remote';
import {api} from 'app/modules/Remote';
import {
  ApiAccessSubscription,
  ApiOrganization,
  ApiOrganizationUser,
} from 'app/modules/Remote/Organization';
import {
  ApiDollarUnitUsage,
  ApiImageryContractWithProjects,
  ApiImageryPurchaseBillingRecord,
} from 'app/modules/Remote/Project';
import colors from 'app/styles/colors.json';
import * as CONSTANTS from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';
import featureFlags from 'app/utils/featureFlags';
import {StatusMaybe} from 'app/utils/hookUtils';
import * as imageryUtils from 'app/utils/imageryUtils';
import {paysAtCheckout} from 'app/utils/organizationUtils';
import * as stringUtils from 'app/utils/stringUtils';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import cs from './ContractHistory.module.scss';
import {CurrentPlanSection} from './CurrentPlanSection';
import {ImageryPaymentSection} from './ImageryPaymentSection';
import {PaymentMethodSection} from './PaymentMethodSection';
import {SubscriptionPaymentSection} from './SubscriptionPaymentSection';
import {appendBillingPeriodsToBillingRecords} from './utils';

const NA_TAG = (
  <B.Tag minimal round>
    N/A
  </B.Tag>
);

const contractHasAnyOrders = (contract: ApiImageryContractWithProjects) =>
  contract.contractType === 'acreUnit'
    ? contract.usage.purchased_acres || contract.usage.voided_acres
    : contract.usage.purchased_acres || contract.usage.voided_acres;

//TODO(eva): potentially generalize this to an UpdateSubscription modal if we have more uses for it
//TODO(eva): break out some of the business logic in calculating total spend into utils-- can consolidate
//with what's used in the ContractTable
const CancelSubscriptionModal: React.FunctionComponent<
  React.PropsWithChildren<{
    subscription: ApiAccessSubscription;
    isOpen: boolean;
    onClose: () => void;
    cancelSubscription: (
      organizationId: string,
      subscriptionId: string,
      comment?: string
    ) => Promise<void>;
    orgId: string;
    contracts: ApiImageryContractWithProjects[];
    organizationUsers: ApiOrganizationUser[] | null;
    projects: any;
  }>
> = ({
  subscription,
  orgId,
  isOpen,
  onClose,
  cancelSubscription,
  contracts,
  organizationUsers,
  projects,
}) => {
  const [cancellationComment, setCancellationComment] = React.useState('');
  const [isCancelling, setIsCancelling] = React.useState(false);

  const totalSpend: number =
    contracts
      ?.map((contract) => {
        const usedCents =
          contract.contractType === CONSTANTS.CONTRACT_TYPE_DOLLAR_UNIT
            ? contract.usage.purchased_cents
            : 0;
        return usedCents && usedCents > 0 ? usedCents : 0;
      })
      .reduce((acc, currentValue) => acc + currentValue, 0) / 100;

  const totalAcres: number = contracts
    ?.map((contract) => {
      const orderedAcres =
        contract.contractType === 'acreUnit'
          ? contract.usage.purchased_acres + contract.usage.voided_acres
          : contract.usage.purchased_acres + contract.usage.voided_acres;
      return orderedAcres && orderedAcres > 0 ? orderedAcres : 0;
    })
    .reduce((acc, currentValue) => acc + currentValue, 0);

  const totalSpendFormatted: string =
    totalSpend > 0
      ? '$' + conversionUtils.numberWithCommas(totalSpend.toFixed(2)) + ' of '
      : 'All ordered ';

  const totalAcresFormatted: string =
    totalAcres > 0
      ? 'across ' + conversionUtils.numberWithCommas(totalAcres.toFixed(0)) + ' acres'
      : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose()}
      primaryButton={{
        text: 'Cancel Subscription',
        intent: B.Intent.DANGER,
        isLoading: isCancelling,
        isDisabled: isCancelling,
      }}
      secondaryButton={{
        text: 'Back',
      }}
      onSubmit={async () => {
        setIsCancelling(true);
        await cancelSubscription(orgId, subscription.id, cancellationComment);
        setIsCancelling(false);
        onClose();
      }}
    >
      <div className={cs.modalContent}>
        <h2>We're sorry to see you go!</h2>
        <p>
          Your subscription is paid through{' '}
          {moment(subscription.currentPeriodEnd!).format('MMMM D, YYYY')}
          {subscription.interval === 'year' &&
            ', and there is a grace period of 30 days after your subscription ends.'}{' '}
          If you would like to proceed with cancelling your subscription, please select "Cancel
          Subscription" below.
        </p>
        <B.Callout intent={B.Intent.WARNING}>
          When your Lens subscription expires, all {organizationUsers?.length || ''} users in your
          account will be unable to access:
          <br />
          <ul>
            <li>
              Imagery, notes, and data throughout {projects?.size ?? 'your'} portfolio
              {projects?.size !== 1 && 's'}
            </li>
            <li>
              {totalSpendFormatted} ordered high-res imagery {totalAcresFormatted}
            </li>
            <li>New downloads, including reports, PNGs, and more</li>
            <li>
              {' '}
              <a
                href="https://lens.tech/support/article/114-payments-and-cancellations"
                target="_blank"
                rel="noopener noreferrer"
              >
                See here for more details
              </a>
            </li>
          </ul>
        </B.Callout>
        <p>Please let us know why you are cancelling:</p>
        <B.TextArea
          large={true}
          fill={true}
          value={cancellationComment}
          onChange={(e) => setCancellationComment(e.target.value)}
        />
      </div>
    </Modal>
  );
};

export const PlanBillingCard: React.FunctionComponent<
  React.PropsWithChildren<{
    accessSubscriptionMaybe: StatusMaybe<ApiAccessSubscription>;
    hasEnded: (end: string | null) => boolean | '' | null;
    currentLensTier: CONSTANTS.LENS_TIERS_TYPE | undefined;
    organization: I.MapAsRecord<I.ImmutableFields<ApiOrganization>>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    cancelSubscription?: (
      organizationId: string,
      subscriptionId: string,
      comment?: string
    ) => Promise<void>;
    contractsMaybe: StatusMaybe<ApiImageryContractWithProjects[]>;
    organizationUsers: ApiOrganizationUser[] | null;
    projects: any;
    isLoadingContracts: boolean;
    imagerySubscriptionIds: string[] | undefined;
  }>
> = ({
  accessSubscriptionMaybe,
  cancelSubscription,
  hasEnded,
  currentLensTier,
  organization,
  profile,
  contractsMaybe,
  organizationUsers,
  projects,
  isLoadingContracts,
  imagerySubscriptionIds,
}) => {
  const organizationId = organization.get('id');
  const hasPermissions = isOwnerOrAdmin(profile.get('role'));
  const canCancelSubscription = featureFlags.CAN_CANCEL_SUBSCRIPTION(profile);
  const orgPaysAtCheckout = paysAtCheckout(organization);
  const managesCardsInApp = orgPaysAtCheckout && hasPermissions;

  const [isLoadingBillingSessionUrl, setIsLoadingBillingSessionUrl] = React.useState(false);
  const [billingSessionUrlError, setBillingSessionUrlError] = React.useState<null | string>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);

  const accessSubscription =
    accessSubscriptionMaybe.status === 'some' && accessSubscriptionMaybe.value;
  const contracts = contractsMaybe.status === 'some' ? contractsMaybe.value : [];

  const hideSubscriptionPaymentSection =
    currentLensTier === 'lens-enterprise' ||
    (accessSubscription && accessSubscription.productKey === 'lens-plus-enterprise');

  return (
    <div className={cs.planBillingCard}>
      <div className={cs.planBillingCardSections}>
        <CurrentPlanSection
          accessSubscriptionMaybe={accessSubscriptionMaybe}
          hasEnded={hasEnded}
          currentLensTier={currentLensTier}
        />
        {!hideSubscriptionPaymentSection && (
          <SubscriptionPaymentSection
            organizationId={organizationId}
            accessSubscriptionMaybe={accessSubscriptionMaybe}
          />
        )}
        <ImageryPaymentSection
          organizationId={organizationId}
          isLoadingContracts={isLoadingContracts}
          imagerySubscriptionIds={imagerySubscriptionIds}
          paysAtCheckout={orgPaysAtCheckout}
        />
        {!managesCardsInApp && <PaymentMethodSection organizationId={organizationId} />}
      </div>
      {managesCardsInApp && <SavedCardsList organizationId={organizationId} />}
      <div className={cs.planBillingCardFooter}>
        <div>
          Shown based on your current subscription terms. We'll notify you in advance if anything
          changes. Please contact <a href="mailto:lens@upstream.tech">lens@upstream.tech</a> with
          any questions.
        </div>
        <div className={cs.planBillingCardActions}>
          <B.Tooltip disabled={hasPermissions} content="Only admins can edit billing info">
            <div style={{display: 'inline-flex', alignItems: 'center', gap: '0.5rem'}}>
              <a
                style={{pointerEvents: hasPermissions ? undefined : 'none'}}
                onClick={async () => {
                  setIsLoadingBillingSessionUrl(true);
                  try {
                    const response = await api.organizations.getBillingSession(
                      organizationId,
                      window.location.href
                    );
                    window.location.href = response.getIn(['data', 'url']);
                  } catch (e) {
                    const err = e as {
                      body: {error: string} | undefined;
                      error: Error;
                      status: number;
                    };
                    setBillingSessionUrlError(err.body?.error || '');
                    setIsLoadingBillingSessionUrl(false);
                  }
                }}
              >
                {managesCardsInApp ? 'Update billing address and tax ID' : 'Update billing info'}
              </a>
              {isLoadingBillingSessionUrl && <B.Spinner size={14} />}
            </div>
          </B.Tooltip>
          {billingSessionUrlError && <div className={cs.errorText}>{billingSessionUrlError}</div>}
          <a href="https://www.upstream.tech/lens/plans" target="_blank" rel="noopener noreferrer">
            Compare plans
          </a>
          {accessSubscription &&
            !accessSubscription.endDate &&
            accessSubscription.currentPeriodEnd &&
            !hasEnded(accessSubscription.currentPeriodEnd) &&
            featureFlags.SHOW_CANCELLATION_LINK(organization, profile, accessSubscription) &&
            cancelSubscription && (
              <>
                <B.Tooltip
                  disabled={canCancelSubscription}
                  content="To cancel this subscription, please contact your account owner or the Lens team."
                >
                  <div className={classnames({[cs.disabledLink]: !canCancelSubscription})}>
                    <a onClick={() => setIsCancelModalOpen(true)}>Cancel subscription</a>
                  </div>
                </B.Tooltip>
                <CancelSubscriptionModal
                  cancelSubscription={cancelSubscription}
                  subscription={accessSubscription}
                  orgId={organizationId}
                  isOpen={isCancelModalOpen}
                  onClose={() => setIsCancelModalOpen(false)}
                  contracts={contracts}
                  organizationUsers={organizationUsers}
                  projects={projects}
                />
              </>
            )}
        </div>
      </div>
    </div>
  );
};

/**
 * Component that handles rendering a loader if we're still fetching data, or
 * the table containing all contracts.
 */
export const ContractHistory: React.FunctionComponent<
  React.PropsWithChildren<{
    contracts: StatusMaybe<ApiImageryContractWithProjects[]>;
    reloadContracts: () => void;
    api?: Pick<typeof Remote.api, 'imagery' | 'organizations'>;
    organizationId: string;
  }>
> = ({contracts, reloadContracts, api = Remote.api, organizationId}) => {
  return (
    <>
      <div className={cs.page}>
        {contracts.status === 'unknown' && (
          <div className={cs.loadingContainer}>
            <B.Spinner />
          </div>
        )}
        {contracts.status === 'error' && (
          <B.NonIdealState
            icon="warning-sign"
            description="We weren’t able to load your contracts right now."
            action={
              <B.Button type="button" text="Try Again" intent="primary" onClick={reloadContracts} />
            }
          />
        )}

        {contracts.status === 'some' && (
          <ContractHistoryTable
            api={api}
            contracts={contracts.value}
            organizationId={organizationId}
          />
        )}
      </div>
    </>
  );
};

/**
 * Table with current and historical contracts.
 */
export const ContractHistoryTable: React.FunctionComponent<
  React.PropsWithChildren<{
    contracts: ApiImageryContractWithProjects[];
    api?: Pick<typeof Remote.api, 'imagery' | 'organizations'>;
    openTooltipsForTest?: boolean;
    organizationId: string;
  }>
> = ({contracts, api = Remote.api, openTooltipsForTest, organizationId}) => {
  const [isDownloadingAll, setIsDownloadingAll] = React.useState(false);

  const [isDownloadingById, setIsDownloadingById] = React.useState({});

  const columns = React.useMemo<Column<ApiImageryContractWithProjects>[]>(
    () => [
      {
        Header: 'Contract',
        accessor: (contract) => contract,
        Cell: ({cell}) => {
          const {name} = cell.value as ApiImageryContractWithProjects;

          const contractStatus = getContractStatus(contracts, cell.value);

          return (
            <div className={cs.contractCell}>
              <B.Icon
                icon="dot"
                // Green for active, gray for completed or inactive.
                color={contractStatus === 'active' ? '#0d8050' : colors.gray}
                htmlTitle={stringUtils.capitalizeFirstLetter(contractStatus)}
              />

              <div>
                {name} {contractStatus === 'completed' && <i>(completed)</i>}
              </div>
            </div>
          );
        },
      },
      {
        Header: 'Portfolio(s)',
        accessor: 'projects',
        Cell: ({cell}) => {
          const projects = cell.value as ApiImageryContractWithProjects['projects'];

          switch (projects.length) {
            case 0:
              return NA_TAG;

            case 1: {
              const {name, isArchived} = projects[0];

              return (
                <div className={cs.portfolioCell} title={isArchived ? `${name} (archived)` : name}>
                  {name} {isArchived && <i>(archived)</i>}
                </div>
              );
            }

            default:
              return (
                <B.Tooltip
                  // Allows popper.js to position the tooltip outside of the
                  // dialog’s bounds. The `hide: {enabled: false}` bit prevents
                  // popper.js from spamming the console with warnings.
                  modifiers={{preventOverflow: {enabled: false}, hide: {enabled: false}}}
                  isOpen={openTooltipsForTest}
                  content={
                    <>
                      <div>Contains orders from {projects.length} portfolios:</div>

                      <ul className={cs.popoverList}>
                        {projects.map(({name, isArchived}) => (
                          <li key={name}>
                            {name} {isArchived && <i>(archived)</i>}
                          </li>
                        ))}
                      </ul>
                    </>
                  }
                >
                  <i>Multiple</i>
                </B.Tooltip>
              );
          }
        },
        width: 100,
      },
      {
        Header: 'Start Date',
        accessor: 'startDate',
        Cell: ({cell}) =>
          imageryUtils.formatDate(cell.value as ApiImageryContractWithProjects['startDate']),
        width: 60,
      },
      {
        Header: 'End Date',
        accessor: 'endDate',
        Cell: ({cell}) => {
          const endDate = cell.value as ApiImageryContractWithProjects['endDate'];

          return endDate ? imageryUtils.formatDate(endDate) : NA_TAG;
        },
        width: 60,
      },
      {
        Header: 'Total Spent',
        accessor: (contract) => contract,
        Cell: ({cell}) =>
          calculateContractSpend(cell.value.usage as ApiDollarUnitUsage, cell.value.contractType),
        width: 60,
      },
      {
        id: 'Total Ordered Acres',
        Header: (
          <div className={cs.orderedAcreageHeader}>
            <div>Total Ordered Acres</div>

            <B.Tooltip
              popoverClassName={cs.popover}
              className={cs.popoverTarget}
              content="Includes paid orders and orders provided at no cost."
              isOpen={openTooltipsForTest}
            >
              <B.Icon icon="info-sign" color={colors.darkestGray} iconSize={14} />
            </B.Tooltip>
          </div>
        ),
        accessor: (contract) => contract,
        Cell: ({cell}) => {
          const contract = cell.value as ApiImageryContractWithProjects;

          const acreage =
            contract.contractType === 'acreUnit'
              ? contract.usage.purchased_acres + contract.usage.voided_acres
              : contract.usage.purchased_acres + contract.usage.voided_acres;

          return conversionUtils.numberWithCommas(acreage.toFixed(0));
        },
        width: 80,
      },
      {
        id: 'Order History',
        Header: () => {
          // finding if there are no contracts that have any usage
          const noOrders = !contracts.find((contract) => contractHasAnyOrders(contract));
          return (
            <div className={cs.ordersHeader}>
              <div>Order History</div>

              <B.Button
                minimal
                title="Download CSV of imagery orders on all contracts"
                icon={
                  <B.Icon
                    icon="download"
                    color={isDownloadingAll ? colors.gray : colors.darkestGray}
                    iconSize={14}
                  />
                }
                disabled={isDownloadingAll || noOrders}
                onClick={async () => {
                  setIsDownloadingAll(true);

                  try {
                    const billingRecords = await Promise.all(
                      contracts.map(
                        async (c) =>
                          (
                            await api.imagery.billingRecords.fetch(
                              c.id,
                              {perPage: 100},
                              {getAllPages: 2}
                            )
                          )
                            .get('data')
                            .toJS() as ApiImageryPurchaseBillingRecord[]
                      )
                    );

                    const billingRecordsWithBillingPeriods =
                      await appendBillingPeriodsToBillingRecords(
                        billingRecords.flat(1),
                        api,
                        organizationId
                      );

                    imageryUtils.exportRecordsCsv({
                      fileName: 'Order History All Contracts',
                      billingRecords: billingRecordsWithBillingPeriods,
                      contracts,
                    });
                  } catch (error) {
                    Sentry.captureException(error);
                    console.error(error);
                    // Let the user know that there has been an issue, and they can try again.
                    window.alert(
                      'We weren’t able to download your CSV right now. Please try again.'
                    );
                  } finally {
                    setIsDownloadingAll(false);
                  }
                }}
              />
            </div>
          );
        },
        accessor: (contract) => contract,
        Cell: ({cell}) => {
          const contract = cell.value as ApiImageryContractWithProjects;
          const {id, name} = contract;

          const isDownloading = isDownloadingById[id];

          const noOrders = !contractHasAnyOrders(contract);

          return (
            <div className={classnames({[cs.disabledDownloadLink]: isDownloading || noOrders})}>
              <a
                title="Download CSV of imagery orders on contract"
                onClick={async () => {
                  setIsDownloadingById((prev) => ({...prev, [id]: true}));

                  try {
                    const billingRecords = (
                      await api.imagery.billingRecords.fetch(id, {perPage: 100}, {getAllPages: 2})
                    )
                      .get('data')
                      .toJS() as ApiImageryPurchaseBillingRecord[];

                    const billingRecordsWithBillingPeriods =
                      await appendBillingPeriodsToBillingRecords(
                        billingRecords,
                        api,
                        organizationId
                      );

                    imageryUtils.exportRecordsCsv({
                      fileName: `Order History ${name}`,
                      billingRecords: billingRecordsWithBillingPeriods,
                      contracts,
                    });
                  } catch (error) {
                    Sentry.captureException(error);
                    console.error(error);
                    // Let the user know that there has been an issue, and they can try again.
                    window.alert(
                      'We weren’t able to download your CSV right now. Please try again.'
                    );
                  } finally {
                    setIsDownloadingById((prev) => ({...prev, [id]: false}));
                  }
                }}
              >
                {isDownloading ? 'Downloading…' : 'Download'}
              </a>
            </div>
          );
        },
        width: 80,
      },
    ],
    [api, contracts, isDownloadingAll, isDownloadingById, openTooltipsForTest]
  );

  const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} = useTable(
    {
      columns,
      data: contracts,
    },
    useFlexLayout
  );

  return (
    <>
      <div className={cs.tableWrapper}>
        <table {...getTableProps()} className={cs.table}>
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((column, i) => (
                  <th {...column.getHeaderProps()} key={i}>
                    <div>{column.render('Header')}</div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);

              return (
                <tr {...row.getRowProps()} key={i}>
                  {row.cells.map((cell, i) => {
                    return (
                      <td {...cell.getCellProps()} key={i}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ContractHistory;

/**
 * Get the contract status. A completed contract has already ended. An inactive
 * contract has either (1) not started yet, or (2) is other than another active
 * contract for the same projects. An active project is in progress, and is the
 * newest active contract for its projects.
 */
export function getContractStatus(
  contracts: ApiImageryContractWithProjects[],
  contract: ApiImageryContractWithProjects
): 'completed' | 'inactive' | 'active' {
  const {startDate, endDate, projects} = contract;

  const today = moment();

  const hasStarted = (start: string) => moment(start).isBefore(today);
  const hasEnded = (end: string | null) => end && moment(end).isBefore(today);

  if (hasEnded(endDate)) {
    return 'completed';
  } else if (!hasStarted(startDate)) {
    return 'inactive';
  } else {
    const contractStartTime = new Date(startDate).getTime();

    // Identify all project contracts that are in progress and have the same
    // projects as the selected contract, and calculate their start times. This
    // helps us identify overlapping active contracts for the same projects, and
    // which one started most recently.
    const activeProjectContractStartTimes = contracts
      .filter(
        (c) => hasStarted(c.startDate) && !hasEnded(c.endDate) && isEqual(c.projects, projects)
      )
      .map((c) => new Date(c.startDate).getTime());

    // Determine if this contract will be the one charges are made against. In
    // addition to not being completed, this contract must have started the most
    // recently of all active contracts for the same projects.
    return contractStartTime === Math.max(...activeProjectContractStartTimes)
      ? 'active'
      : 'inactive';
  }
}

/**
 * Create a display of the total spend of a contract (or, display a tooltip referring
 * the user to the CSV to find the spend there for legacy acreage-based contracts).
 */
export function calculateContractSpend(
  usage: ApiDollarUnitUsage,
  contractType: 'dollarUnit' | 'acreUnit'
): string | React.ReactElement {
  switch (contractType) {
    case CONSTANTS.CONTRACT_TYPE_DOLLAR_UNIT:
      return '$' + conversionUtils.numberWithCommas((usage.purchased_cents / 100).toFixed(2));
    case CONSTANTS.CONTRACT_TYPE_ACRE_UNIT:
      return (
        <B.Tooltip content={'Download the CSV to view total spent for this contract.'}>
          {NA_TAG}
        </B.Tooltip>
      );
    default:
      // In practice, we don't hit this case because all of our contract types
      // are covered by the switch cases.
      return <></>;
  }
}
